import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface FlexESimulationSummary {
  id: string;
  name: string;
  created_at: string;
  success_rate: number | null;
  passed_days: number | null;
  failed_days: number | null;
  total_days: number | null;
  batt_kw: number;
  batt_kwh: number | null;
  cap_limited_kw: number;
  window_start: string;
  window_end: string;
}

interface UseFlexESimulationsFilters {
  search?: string;
  minSuccessRate?: number;
  maxSuccessRate?: number;
  dateFrom?: Date;
  dateTo?: Date;
  onlyFailed?: boolean;
}

export function useFlexESimulations(filters?: UseFlexESimulationsFilters) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all simulations with filters
  const { data: simulations, isLoading, error, refetch } = useQuery({
    queryKey: ['flexe-simulations', filters],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Geen .eq('tenant_id', user.id) meer. Die filter deed het werk van RLS
      // nog eens over, maar dan strenger: hij liet uitsluitend je eigen rijen
      // door. Gemeten 08-09-2026: alle 7 simulaties staan op één gebruiker, dus
      // Lieke, Niels en Dylan kregen een leeg scherm — en een admin ook.
      // Migratie 061 zet het leesrecht op de drie CRM-rollen; wie wat mag zien
      // hoort daar te worden bepaald, op één plek, en niet hier nog een keer.
      let query = supabase
        .from('flexe_simulations')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply search filter
      if (filters?.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      // Apply success rate filters
      if (filters?.minSuccessRate !== undefined) {
        query = query.gte('success_rate', filters.minSuccessRate);
      }
      if (filters?.maxSuccessRate !== undefined) {
        query = query.lte('success_rate', filters.maxSuccessRate);
      }

      // Apply date filters
      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom.toISOString());
      }
      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo.toISOString());
      }

      // Apply only failed filter
      if (filters?.onlyFailed) {
        query = query.lt('success_rate', 95);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return data as FlexESimulationSummary[];
    }
  });

  // Delete single simulation
  const deleteMutation = useMutation({
    mutationFn: async (simId: string) => {
      // `.select()` erbij, want zonder dat geeft PostgREST `error: null` terug
      // wanneer RLS de rij tegenhoudt — nul rijen geraakt, geen fout, en de
      // toast meldde vrolijk "succesvol verwijderd" terwijl er niets gebeurde.
      const { data, error } = await supabase
        .from('flexe_simulations')
        .delete()
        .eq('id', simId)
        .select('id');

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Deze simulatie is niet verwijderd: je hebt er geen rechten op.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flexe-simulations'] });
      toast({
        title: 'Simulatie verwijderd',
        description: 'De simulatie is succesvol verwijderd'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Verwijderen mislukt',
        description: error.message || 'Er is een fout opgetreden',
        variant: 'destructive'
      });
    }
  });

  // Bulk delete simulations
  const bulkDeleteMutation = useMutation({
    mutationFn: async (simIds: string[]) => {
      const { error } = await supabase
        .from('flexe_simulations')
        .delete()
        .in('id', simIds);
      
      if (error) throw error;
    },
    onSuccess: (_, simIds) => {
      queryClient.invalidateQueries({ queryKey: ['flexe-simulations'] });
      toast({
        title: 'Simulaties verwijderd',
        description: `${simIds.length} simulatie${simIds.length > 1 ? 's' : ''} succesvol verwijderd`
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Verwijderen mislukt',
        description: error.message || 'Er is een fout opgetreden',
        variant: 'destructive'
      });
    }
  });

  // Rename simulation
  const renameMutation = useMutation({
    mutationFn: async ({ simId, newName }: { simId: string; newName: string }) => {
      // Zelfde reden als bij verwijderen: zonder `.select()` is een door RLS
      // geweigerde update niet van een geslaagde te onderscheiden.
      const { data, error } = await supabase
        .from('flexe_simulations')
        .update({ name: newName })
        .eq('id', simId)
        .select('id');

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('De naam is niet gewijzigd: je hebt geen rechten op deze simulatie.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flexe-simulations'] });
      toast({
        title: 'Naam gewijzigd',
        description: 'De simulatienaam is bijgewerkt'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Wijzigen mislukt',
        description: error.message || 'Er is een fout opgetreden',
        variant: 'destructive'
      });
    }
  });

  return {
    simulations: simulations || [],
    isLoading,
    error,
    refetch,
    deleteSimulation: deleteMutation.mutate,
    bulkDeleteSimulations: bulkDeleteMutation.mutate,
    renameSimulation: renameMutation.mutate,
    isDeleting: deleteMutation.isPending || bulkDeleteMutation.isPending,
    isRenaming: renameMutation.isPending
  };
}
